import React from 'react';
import { SurveyScopeEntityType, SurveyScopeExclusion } from '../../../../../common/src/domain/surveyScopeExclusion/surveyScopeExclusionTableSchema';
import { OpenApiRouteResultType } from '../../../../../common/src/routes/openApiTypes';
import { fetchJson } from '../../../store/fetch';

export interface IExclusionInput {
    entityType: SurveyScopeEntityType;
    entityId: string;
}

export interface IScopeData {
    definedScope: string | null;
    exclusions: SurveyScopeExclusion[];
}

export interface IUseScopeResult {
    definedScope: string | null;
    exclusions: SurveyScopeExclusion[];
    isLoading: boolean;
    refresh: () => Promise<void>;
}

type ScopeResponse = OpenApiRouteResultType<'/api/surveys/{surveyId}/scope', 'get'>;

export function useScope(surveyId: string | undefined): IUseScopeResult {
    const [definedScope, setDefinedScope] = React.useState<string | null>(null);
    const [exclusions, setExclusions] = React.useState<SurveyScopeExclusion[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(Boolean(surveyId));

    const refresh = React.useCallback(async () => {
        if (!surveyId) {
            return;
        }
        setIsLoading(true);
        try {
            const result = await fetchJson(`/api/surveys/${surveyId}/scope`, 'GET');
            const json = result.json as ScopeResponse | undefined;
            if (result.successful && json) {
                setDefinedScope(json.definedScope ?? null);
                setExclusions(json.exclusions as SurveyScopeExclusion[]);
            }
        } finally {
            setIsLoading(false);
        }
    }, [surveyId]);

    React.useEffect(() => {
        void refresh();
    }, [refresh]);

    return { definedScope, exclusions, isLoading, refresh };
}

export async function saveScope(surveyId: string, definedScope: string | null, exclusions: IExclusionInput[]): Promise<boolean> {
    const body = {
        definedScope,
        exclusions: exclusions.map((e) => ({ entityType: e.entityType, entityId: e.entityId })),
    };
    const result = await fetchJson(`/api/surveys/${surveyId}/scope`, 'PUT', body);
    return result.successful;
}
